import * as React from "react"

type Theme = "light" | "dark" | "system"

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = React.createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
})

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = React.useState<Theme>(() => {
    return (localStorage.getItem("cartera-theme") as Theme) ?? "light"
  })

  React.useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "dark")

    let effectiveTheme: "light" | "dark" = theme === "dark" ? "dark" : "light"
    if (theme === "system") {
      effectiveTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      root.classList.add(effectiveTheme)
    } else {
      root.classList.add(theme)
    }

    // Keep meta theme-color in sync for mobile Safari and iOS PWA status bar
    const targetColor = effectiveTheme === "dark" ? "#090d16" : "#ffffff"
    const metaThemeColors = document.querySelectorAll('meta[name="theme-color"]')
    metaThemeColors.forEach((meta) => {
      meta.setAttribute("content", targetColor)
    })

    localStorage.setItem("cartera-theme", theme)
  }, [theme])

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => React.useContext(ThemeContext)
